import React, { useEffect, useRef, useState } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { useMutation } from 'react-query';
import { useNavigate, useLocation } from 'react-router';
import { modalURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import { GoggleApi } from '@jwp/ott-common/src/env';
import { type LibraryBranch, type LibraryItem } from '@jwp/ott-common/src/controllers/graphql';
import { fetchUserLocation } from '@jwp/ott-common/src/controllers/graphql';

import icon from '../../../../theme/assets/icons/Icon.svg';
import defaultIcon from '../../../../theme/assets/icons/default.svg';

import styles from './Mapview.module.scss';

interface WindowWithGoogle extends Window {
  google: any;
}

type Props = {
  libraryLists?: LibraryItem[];
  seleteLocation?: any;
};

const GoogleMap: React.FC<Props> = ({ libraryLists, seleteLocation }) => {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const clickedIconUrl = icon;
  const navigate = useNavigate();
  const location = useLocation();
  const storedLocation = sessionStorage.getItem('userLocation');
  const { state } = location;

  const { libraryListData, locationRef } = state ?? JSON.parse(storedLocation as any);
  const [userLocation, setUserLocation] = useState({
    latitude: 0,
    longitude: 0,
  });

  const currentInfoWindowRef = useRef<google.maps.InfoWindow | null>(null);

  const LocationMutation = useMutation({
    mutationFn: fetchUserLocation,
    onError: (error) => {
      console.error('LocationMutation error:', error);
    },
    onSuccess: (data: any) => {
      if (data) {
        const { longitude, latitude } = data.GetLocationByIP;
        setUserLocation({ latitude, longitude });
      }
    },
  });

  useEffect(() => {
    LocationMutation.mutate();
  }, []);

  useEffect(() => {
    if (!userLocation.latitude || !userLocation.longitude) return;

    const loader = new Loader({
      apiKey: GoggleApi.API_KEY,
      version: 'weekly',
    });

    loader.load().then(() => {
      const google = (window as WindowWithGoogle).google;

      const selectedLat = seleteLocation?.current?.libraryLat || locationRef?.current?.libraryLat || Number(userLocation.latitude);
      const selectedLon = seleteLocation?.current?.libraryLon || locationRef?.current?.libraryLon || Number(userLocation.longitude);

      const map = new google.maps.Map(mapRef.current, {
        center: {
          lat: selectedLat,
          lng: selectedLon,
        },
        zoom: 8,
        panControl: false,
        mapTypeControl: false,
        streetViewControl: false,
        disableDefaultUI: true,
        scrollWheel: true,
        draggable: true,
        zoomControl: true,
        fullscreenControl: false,
      });

      mapInstanceRef.current = map;

      const markers: google.maps.Marker[] = [];

      const libraries = libraryLists || libraryListData;
      if (libraries) {
        libraries.forEach((param: LibraryItem) => {
          param.libraryBranch.forEach((branch: LibraryBranch) => {
            const marker = new google.maps.Marker({
              position: { lat: branch.libraryLat, lng: branch.libraryLon },
              map: map,
              icon: {
                url: defaultIcon,
                scaledSize: new google.maps.Size(30, 30),
              },
            });

            markers.push(marker);

            const infoWindow = new google.maps.InfoWindow({
              content: `
              <style>
                .gm-style-iw { padding: 0 !important; }
                .gm-ui-hover-effect { display: none !important; }
                .gm-style-iw-ch { padding: 0 !important; }
                .custom-info-window { font-family: Arial, sans-serif; width: 260px; }
                .info-header { display: flex; justify-content: space-between; align-items: center; background-color: #233b2d; color: white; padding: 5px 10px; font-size: 14px; font-weight: bold; gap: 12px; }
                .info-body { padding: 10px; }
                .select-library-btn { background-color: #FFBA30; color: white; border-radius: 0; padding: 8px 16px; cursor: pointer; font-size: 14px; margin-top: 10px; width: 100%; }
                .close-btn { cursor: pointer; font-size: 18px; color: white; }
                .parentName { color: #233b2d; font-weight: 500; font-size: 1rem; text-transform: capitalize; }
                .headingText { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-size: 12px; font-weight: 700; text-transform: capitalize; }
              </style>
              <div class="custom-info-window">
                <div class="info-header">
                  <h4 class="headingText">${param.libraryParent.libraryName.toLowerCase()}</h4>
                  <span class="close-btn">✕</span>
                </div>
                <div class="info-body">
                  <p class="parentName">${branch.libraryName.toLowerCase()}</p>
                  <button class="select-library-btn">Select Library</button>
                </div>
              </div>
            `,
            });

            const openInfoWindow = () => {
              if (currentInfoWindowRef.current) {
                currentInfoWindowRef.current.close();
              }
              infoWindow.open(map, marker);
              currentInfoWindowRef.current = infoWindow;
            };

            marker.addListener('click', openInfoWindow);

            marker.addListener('mouseover', () => {
              marker.setIcon({
                url: clickedIconUrl,
                scaledSize: new google.maps.Size(30, 30),
              });
            });

            marker.addListener('mouseout', () => {
              marker.setIcon({
                url: defaultIcon,
                scaledSize: new google.maps.Size(30, 30),
              });
            });

            infoWindow.addListener('domready', () => {
              const closeButton = document.querySelector('.close-btn');
              closeButton?.addEventListener('click', () => {
                infoWindow.close();
              });

              const selectButton = document.querySelector('.select-library-btn');
              selectButton?.addEventListener('click', () => {
                navigate(modalURLFromLocation(location, 'library-access'), {
                  state: {
                    libraryId: branch.libraryId,
                    libraryName: branch.libraryName,
                    libraryParent: param.restriction,
                    withInOrganization: param.withInOrganization,
                  },
                });
                infoWindow.close();
                sessionStorage.removeItem('userLocation');
              });
            });

            const shouldOpenInfoWindow =
              (seleteLocation?.current?.libraryLat === branch?.libraryLat && seleteLocation?.current?.libraryLon === branch?.libraryLon) ||
              (locationRef?.current?.libraryLat === branch?.libraryLat && locationRef?.current?.libraryLon === branch?.libraryLon);

            if (shouldOpenInfoWindow) {
              openInfoWindow();
            }
          });
        });
      }
    });

    return () => {
      if (mapInstanceRef.current) {
        google.maps.event.clearInstanceListeners(mapInstanceRef.current);
      }
    };
  }, [
    userLocation,
    libraryLists,
    seleteLocation?.current?.libraryLat,
    locationRef,
    clickedIconUrl,
    location,
    navigate,
    libraryListData,
    seleteLocation,
    seleteLocation?.current?.libraryLon,
  ]);

  useEffect(() => {
    if (mapInstanceRef.current && userLocation.latitude && userLocation.longitude) {
      const newCenter = {
        lat: userLocation.latitude,
        lng: userLocation.longitude,
      };
      mapInstanceRef.current.panTo(newCenter);
    }
  }, [userLocation]);

  return (
    <>
      <div ref={mapRef} className={styles.map} />
    </>
  );
};

export default GoogleMap;
